'use client'

import * as React from 'react'
import { useState, useMemo, useCallback } from 'react'
import { AddRegular, BuildingMultipleRegular, CallRegular, ChatRegular, ChevronDownRegular, DismissRegular, EyeRegular, FilterRegular, MailRegular, MoreHorizontalRegular, PeopleRegular, SearchRegular, SpinnerIosRegular } from '@fluentui/react-icons'
import { cn } from '@repo/ui'
import {
  Button,
  Badge,
  Input,
  UserAvatar,
  Card,
  CardContent,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui'
import type { Customer, CustomerStatus, CustomerType, CustomerFilters } from '../../entities/customer'

/** A UUID-looking string is not a useful display name. Some backend
 *  paths set `displayName`/`name` to the customer id when the record
 *  has no real identity yet — filter those out. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isHumanLabel(value: string | null | undefined): value is string {
  return !!value && value.length > 0 && !UUID_RE.test(value)
}

/** Pick the best human-readable label for a customer row:
 *  `displayName → name → phone → email → "—"`. Each candidate is
 *  filtered through `isHumanLabel` so we never render a raw UUID. */
export function customerDisplayLabel(customer: Customer): string {
  if (isHumanLabel(customer.displayName)) return customer.displayName
  if (isHumanLabel(customer.name)) return customer.name
  if (isHumanLabel(customer.phone)) return customer.phone
  if (isHumanLabel(customer.email)) return customer.email
  return '—'
}

export interface CustomerListProps {
  customers: Customer[]
  totalCount?: number
  filters?: CustomerFilters
  isLoading?: boolean
  onFilterChange?: (filters: CustomerFilters) => void
  onCustomerClick?: (customer: Customer) => void
  /** Optional quick-preview handler. When provided, the row's overflow
   * menu shows a "Preview" item that fires this instead of navigating. */
  onCustomerPreview?: (customer: Customer) => void
  onCustomerCall?: (customer: Customer) => void
  onCustomerEmail?: (customer: Customer) => void
  onCustomerEdit?: (customer: Customer) => void
  onCustomerDelete?: (customer: Customer) => void
  onAddCustomer?: () => void
  onLoadMore?: () => void
  hasMore?: boolean
  viewMode?: 'table' | 'cards'
  className?: string
  /** Translation function */
  t: (key: string) => string
}

// Memoized: a 200-row list re-rendered every parent state change was
// the main UI hitch on this page. Wrapped with React.memo so each row
// only re-renders when its own customer object or callbacks change.
const CustomerCard = React.memo(function CustomerCard({
  customer,
  onClick,
  onPreview,
  onCall,
  onEmail,
  onEdit,
  onDelete,
  t,
}: {
  customer: Customer
  onClick?: () => void
  onPreview?: () => void
  onCall?: () => void
  onEmail?: () => void
  onEdit?: () => void
  onDelete?: () => void
  t: (key: string) => string
}) {
  const display = customerDisplayLabel(customer)
  return (
    <Card
      data-testid="customer-card"
      data-customer-id={customer.id}
      className="cursor-pointer transition-colors border-border/50 hover:bg-muted/30"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <UserAvatar src={customer.avatar} name={display} size="lg" />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate font-semibold">{display}</h3>
              {customer.segment && (
                <Badge variant="outline">{customer.segment}</Badge>
              )}
            </div>

            {customer.companyId && (
              <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                <BuildingMultipleRegular className="h-3 w-3" />
                {customer.company || customer.companyId}
              </p>
            )}

            <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
              {customer.email && (
                <span className="flex items-center gap-1">
                  <MailRegular className="h-3 w-3" />
                  {customer.email}
                </span>
              )}
              {customer.phone && (
                <span className="flex items-center gap-1">
                  <CallRegular className="h-3 w-3" />
                  {customer.phone}
                </span>
              )}
            </div>

            {typeof customer.interactionCount === 'number' && customer.interactionCount > 0 && (
              <div className="mt-3 flex items-center gap-1 text-sm text-muted-foreground">
                <ChatRegular className="h-4 w-4" />
                {customer.interactionCount}
              </div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon-sm" aria-label={t('actions.menu')}>
                <MoreHorizontalRegular className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {onPreview && (
                <DropdownMenuItem onClick={onPreview}>
                  <EyeRegular className="mr-2 h-4 w-4" />
                  {t('actions.preview')}
                </DropdownMenuItem>
              )}
              {onPreview && (onCall || onEmail || onEdit || onDelete) && (
                <DropdownMenuSeparator />
              )}
              {onCall && customer.phone && (
                <DropdownMenuItem onClick={onCall}>
                  <CallRegular className="mr-2 h-4 w-4" />
                  {t('actions.call')}
                </DropdownMenuItem>
              )}
              {onEmail && customer.email && (
                <DropdownMenuItem onClick={onEmail}>
                  <MailRegular className="mr-2 h-4 w-4" />
                  {t('actions.email')}
                </DropdownMenuItem>
              )}
              {(onCall || onEmail) && (onEdit || onDelete) && <DropdownMenuSeparator />}
              {onEdit && (
                <DropdownMenuItem onClick={onEdit}>
                  {t('actions.edit')}
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem onClick={onDelete} intent="danger">
                  {t('actions.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
})
CustomerCard.displayName = 'CustomerCard'

export function CustomerList({
  customers,
  totalCount,
  filters = {},
  isLoading = false,
  onFilterChange,
  onCustomerClick,
  onCustomerPreview,
  onCustomerCall,
  onCustomerEmail,
  onCustomerEdit,
  onCustomerDelete,
  onAddCustomer,
  onLoadMore,
  hasMore = false,
  viewMode = 'table',
  className,
  t,
}: CustomerListProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '')
  const [showFilters, setShowFilters] = useState(false)

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value)
      onFilterChange?.({ ...filters, search: value })
    },
    [filters, onFilterChange]
  )

  const handleStatusFilter = useCallback(
    (value: string) => {
      onFilterChange?.({
        ...filters,
        status: value === 'all' ? undefined : (value as CustomerStatus),
      })
    },
    [filters, onFilterChange]
  )

  const handleTypeFilter = useCallback(
    (value: string) => {
      onFilterChange?.({
        ...filters,
        type: value === 'all' ? undefined : (value as CustomerType),
      })
    },
    [filters, onFilterChange]
  )

  const clearFilters = useCallback(() => {
    setSearchValue('')
    onFilterChange?.({})
  }, [onFilterChange])

  const hasActiveFilters = filters.search || filters.status || filters.type

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Input
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('searchPlaceholder')}
            leftIcon={<SearchRegular />}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(hasActiveFilters && 'border-primary')}
          >
            <FilterRegular className="mr-2 h-4 w-4" />
            {t('filters')}
            {hasActiveFilters && (
              <Badge color="secondary" className="ml-2">
                {[filters.status, filters.type].filter(Boolean).length}
              </Badge>
            )}
          </Button>

          {onAddCustomer && (
            <Button onClick={onAddCustomer} data-testid="customer-add-btn">
              <AddRegular className="mr-2 h-4 w-4" />
              {t('addCustomer')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t('statusLabel')}</span>
                <Select
                  value={filters.status || 'all'}
                  onValueChange={handleStatusFilter}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filterAll')}</SelectItem>
                    <SelectItem value="active">{t('statuses.active')}</SelectItem>
                    <SelectItem value="inactive">{t('statuses.inactive')}</SelectItem>
                    <SelectItem value="prospect">{t('statuses.prospect')}</SelectItem>
                    <SelectItem value="churned">{t('statuses.churned')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t('typeLabel')}</span>
                <Select
                  value={filters.type || 'all'}
                  onValueChange={handleTypeFilter}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filterAll')}</SelectItem>
                    <SelectItem value="individual">{t('types.individual')}</SelectItem>
                    <SelectItem value="business">{t('types.business')}</SelectItem>
                    <SelectItem value="enterprise">{t('types.enterprise')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <DismissRegular className="mr-2 h-4 w-4" />
                  {t('resetFilters')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results count */}
      {totalCount !== undefined && (
        <p className="text-sm text-muted-foreground">
          {t('customersFound')}: {totalCount}
        </p>
      )}

      {/* Loading */}
      {isLoading && customers.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <SpinnerIosRegular className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state — uses EmptyState primitive (info variant). The
       * call-to-action is the same Add Customer button used in the
       * page header, so first-time users always have an obvious next
       * step. */}
      {!isLoading && customers.length === 0 && (
        <EmptyState
          variant="info"
          icon={<PeopleRegular className="h-8 w-8" />}
          title={t('noCustomersFound')}
          description={hasActiveFilters ? t('tryAdjustFilters') : t('addFirstCustomer')}
          action={
            onAddCustomer && !hasActiveFilters ? (
              <Button onClick={onAddCustomer} data-testid="customer-add-btn">
                <AddRegular className="mr-2 h-4 w-4" />
                {t('addCustomer')}
              </Button>
            ) : undefined
          }
        />
      )}

      {/* Table view */}
      {viewMode === 'table' && customers.length > 0 && (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border bg-muted/30">
                <TableHead className="w-[34%]">{t('table.customer')}</TableHead>
                <TableHead className="w-[26%]">{t('table.tags')}</TableHead>
                <TableHead className="w-[16%]">{t('table.joined')}</TableHead>
                <TableHead className="w-[16%] text-right">{t('table.interactions')}</TableHead>
                <TableHead className="w-[8%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => {
                const display = customerDisplayLabel(customer)
                return (
                <TableRow
                  key={customer.id}
                  data-testid="customer-row"
                  data-customer-id={customer.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors border-b border-border/50"
                  onClick={() => onCustomerClick?.(customer)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar src={customer.avatar} name={display} size="sm" />
                      <div className="min-w-0">
                        <p className="truncate font-medium">{display}</p>
                        {customer.companyId && (
                          <p className="truncate text-xs text-muted-foreground">{customer.company || customer.companyId}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {customer.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} color="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {customer.tags.length > 3 && (
                        <Badge color="secondary" className="text-xs">+{customer.tags.length - 3}</Badge>
                      )}
                      {customer.tags.length === 0 && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {customer.createdAt
                      ? new Date(customer.createdAt).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {customer.interactionCount ?? 0}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon-sm" aria-label={t('actions.menu')}>
                          <MoreHorizontalRegular className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {onCustomerPreview && (
                          <>
                            <DropdownMenuItem onClick={() => onCustomerPreview(customer)}>
                              <EyeRegular className="mr-2 h-4 w-4" />
                              {t('actions.preview')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        {onCustomerCall && customer.phone && (
                          <DropdownMenuItem onClick={() => onCustomerCall(customer)}>
                            <CallRegular className="mr-2 h-4 w-4" />
                            {t('actions.call')}
                          </DropdownMenuItem>
                        )}
                        {onCustomerEmail && customer.email && (
                          <DropdownMenuItem onClick={() => onCustomerEmail(customer)}>
                            <MailRegular className="mr-2 h-4 w-4" />
                            {t('actions.email')}
                          </DropdownMenuItem>
                        )}
                        {onCustomerEdit && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => onCustomerEdit(customer)}>
                              {t('actions.edit')}
                            </DropdownMenuItem>
                          </>
                        )}
                        {onCustomerDelete && (
                          <DropdownMenuItem
                            onClick={() => onCustomerDelete(customer)}
                            className="text-destructive"
                          >
                            {t('actions.delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Cards view */}
      {viewMode === 'cards' && customers.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {customers.map((customer) => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onClick={() => onCustomerClick?.(customer)}
              onPreview={onCustomerPreview ? () => onCustomerPreview(customer) : undefined}
              onCall={onCustomerCall ? () => onCustomerCall(customer) : undefined}
              onEmail={onCustomerEmail ? () => onCustomerEmail(customer) : undefined}
              onEdit={onCustomerEdit ? () => onCustomerEdit(customer) : undefined}
              onDelete={onCustomerDelete ? () => onCustomerDelete(customer) : undefined}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoading}
          >
            {isLoading ? (
              <SpinnerIosRegular className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ChevronDownRegular className="mr-2 h-4 w-4" />
            )}
            {t('loadMore')}
          </Button>
        </div>
      )}
    </div>
  )
}
